// Admin › Settings › Referral Program — the rewards program terms (rewards part 2).
// Spec: docs/superpowers/specs/2026-11-03-rewards-program-design.md → "Program terms"
//
// The program switch, the current numbers (saved as a new version with a note,
// never edited in place), the trial for sellers who skip the entry fee, and the
// version history with what changed each time.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FIELD_KIND,
  REWARDS_ENABLED_KEY,
  RewardsApiError,
  TERMS_FIELDS,
  TERMS_GROUPS,
  TRIAL_DAYS_KEY,
  fetchTerms,
  putConfig,
  saveTerms,
  termsKey,
  type TermsField,
  type TermsValues,
  type TermsVersion,
} from '../../../api/rewards'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Field } from '../../../components/ui/field'
import { Input } from '../../../components/ui/input'
import { useT } from '../../../i18n'
import { rewardsTermsI18n, type RewardsTermsStrings } from '../../../i18n/admin/rewards-terms'
import { formatCurrency } from '../../../lib/currency'
import { formatDate } from '../../../lib/datetime'

const TEXTAREA_CLASS =
  'w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] text-[var(--field-text)] placeholder:text-[var(--field-placeholder)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)]'

const UNIT: Record<TermsField, keyof RewardsTermsStrings['units']> = {
  entry_fee_usd: 'usd',
  entry_credit_usd: 'usd',
  entry_trial_days: 'days',
  qualify_min_days: 'days',
  qualify_min_shipped_orders: 'orders',
  threshold_count: 'referrals',
  threshold_reward_usd: 'usd',
  per_referral_reward_usd: 'usd',
  reward_credit_expiry_months: 'months',
  commission_percent: 'percent',
  commission_period_days: 'days',
  conversion_bonus_percent: 'percent',
}

type Draft = Record<TermsField, string>

function toDraft(v: TermsValues): Draft {
  return Object.fromEntries(TERMS_FIELDS.map((f) => [f, String(v[f])])) as Draft
}

function parse(field: TermsField, raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  if (FIELD_KIND[field] === 'count' && !Number.isInteger(n)) return null
  if (FIELD_KIND[field] === 'percent' && n > 100) return null
  return n
}

function display(field: TermsField, value: string | number, t: RewardsTermsStrings): string {
  const n = Number(value)
  if (FIELD_KIND[field] === 'money') return formatCurrency(n, 'USD')
  if (FIELD_KIND[field] === 'percent') return t.percentValue(n)
  return `${n} ${t.units[UNIT[field]]}`
}

function changesBetween(
  before: TermsValues,
  after: TermsValues,
): Array<{ field: TermsField; from: string | number; to: string | number }> {
  return TERMS_FIELDS.filter((f) => Number(before[f]) !== Number(after[f])).map((f) => ({
    field: f,
    from: before[f],
    to: after[f],
  }))
}

function fmtDate(iso: string): string {
  return formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' })
}

function SwitchDialog({
  enabled,
  onClose,
  t,
}: {
  enabled: boolean
  onClose: () => void
  t: RewardsTermsStrings
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => putConfig(REWARDS_ENABLED_KEY, !enabled),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: termsKey })
      onClose()
    },
  })
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{enabled ? t.switch.offTitle : t.switch.onTitle}</DialogTitle>
          <DialogDescription>{enabled ? t.switch.offBody : t.switch.onBody}</DialogDescription>
        </DialogHeader>
        {mutation.isError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {(mutation.error as Error).message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.switch.cancel}
          </Button>
          <Button
            variant={enabled ? 'destructive' : 'primary'}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {enabled ? t.switch.confirmOff : t.switch.confirmOn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SaveDialog({
  current,
  values,
  onClose,
  onSaved,
  t,
}: {
  current: TermsVersion
  values: TermsValues
  onClose: () => void
  onSaved: (row: TermsVersion) => void
  t: RewardsTermsStrings
}) {
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)
  const changes = changesBetween(current, values)
  const noteOk = note.trim().length >= 3
  const mutation = useMutation({
    mutationFn: () => saveTerms(values, note.trim()),
    onSuccess: onSaved,
  })
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent mobilePresentation="fullscreen" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.saveDialog.title}</DialogTitle>
          <DialogDescription>{t.saveDialog.body}</DialogDescription>
        </DialogHeader>
        <div>
          <div className="text-xs font-medium text-[var(--color-text-muted)]">
            {t.saveDialog.changes}
          </div>
          <ul className="mt-1 space-y-1 text-sm">
            {changes.map((c) => (
              <li key={c.field} className="flex flex-wrap justify-between gap-x-3">
                <span className="text-[var(--color-text)]">{t.fields[c.field]}</span>
                <span className="tabular-nums text-[var(--color-text-muted)]">
                  {display(c.field, c.from, t)} → {' '}
                  <span className="font-medium text-[var(--color-text)]">
                    {display(c.field, c.to, t)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <Field
          id="rewards-terms-note"
          label={t.saveDialog.noteLabel}
          required
          error={touched && !noteOk ? t.saveDialog.noteRequired : undefined}
        >
          <textarea
            rows={3}
            maxLength={500}
            className={TEXTAREA_CLASS}
            placeholder={t.saveDialog.notePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        {mutation.isError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {mutation.error instanceof RewardsApiError && mutation.error.code === 'reward_terms_unchanged'
              ? t.unchanged
              : (mutation.error as Error).message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.saveDialog.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={mutation.isPending}
            onClick={() => {
              setTouched(true)
              if (noteOk) mutation.mutate()
            }}
          >
            {mutation.isPending ? t.saveDialog.saving : t.saveDialog.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TrialSetting({ days, t }: { days: number; t: RewardsTermsStrings }) {
  const queryClient = useQueryClient()
  // null = show the saved value; typing keeps a local draft until it is saved.
  const [edit, setEdit] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const draft = edit ?? String(days)
  const n = Number(draft)
  const valid = draft.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 365
  const mutation = useMutation({
    mutationFn: () => putConfig(TRIAL_DAYS_KEY, n),
    onSuccess: async () => {
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: termsKey })
      setEdit(null)
    },
  })
  return (
    <div className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-3">
      <div className="text-sm font-medium text-[var(--color-text)]">{t.trial.title}</div>
      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{t.trial.hint}</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <Field
          id="rewards-default-trial"
          label={t.trial.label}
          error={!valid ? t.invalid : undefined}
          className="w-36"
        >
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            step={1}
            value={draft}
            onChange={(e) => {
              setSaved(false)
              setEdit(e.target.value)
            }}
            className="tabular-nums"
          />
        </Field>
        <span className="pb-2 text-xs text-[var(--color-text-muted)]">{t.units.days}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={!valid || n === days || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="mb-1"
        >
          {t.trial.save}
        </Button>
        {saved && <span className="pb-2 text-xs text-[var(--color-success)]">{t.trial.saved}</span>}
      </div>
    </div>
  )
}

function History({ versions, t }: { versions: TermsVersion[]; t: RewardsTermsStrings }) {
  return (
    <details className="group rounded-[var(--radius-panel)] border border-[var(--color-border)]">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]">
        {t.history.title(versions.length)}
      </summary>
      <ol className="divide-y divide-[var(--color-border)] border-t border-[var(--color-border)]">
        {versions.map((v, i) => {
          const previous = versions[i + 1]
          const changes = previous ? changesBetween(previous, v) : []
          return (
            <li key={v.id} className="px-3 py-2 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="font-medium text-[var(--color-text)]">
                  {t.versionLine(v.version, fmtDate(v.created_at), v.created_by_email)}
                </span>
              </div>
              <p className="mt-0.5 text-[var(--color-text-muted)]">{v.note}</p>
              <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-text-muted)]">
                {previous === undefined ? (
                  <li>{t.history.first}</li>
                ) : changes.length === 0 ? (
                  <li>{t.history.noChanges}</li>
                ) : (
                  changes.map((c) => (
                    <li key={c.field} className="tabular-nums">
                      {t.fields[c.field]}: {display(c.field, c.from, t)} →{' '}
                      {display(c.field, c.to, t)}
                    </li>
                  ))
                )}
              </ul>
            </li>
          )
        })}
      </ol>
    </details>
  )
}

export function RewardsTermsCard() {
  const t = useT(rewardsTermsI18n)
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: termsKey, queryFn: fetchTerms })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [switching, setSwitching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedVersion, setSavedVersion] = useState<number | null>(null)

  const current = query.data?.current
  const base = useMemo(() => (current ? toDraft(current) : null), [current])
  const values = draft ?? base

  if (query.isLoading) {
    return (
      <Card className="p-4 text-sm text-[var(--color-text-muted)]">{t.loading}</Card>
    )
  }
  if (query.isError || !query.data || !current || !values || !base) {
    return (
      <Card className="flex flex-wrap items-center gap-3 p-4 text-sm">
        <span role="alert" className="text-[var(--color-danger)]">
          {t.loadError}
        </span>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          {t.retry}
        </Button>
      </Card>
    )
  }

  const { enabled, versions, default_trial_days } = query.data
  const parsed = Object.fromEntries(TERMS_FIELDS.map((f) => [f, parse(f, values[f])])) as Record<
    TermsField,
    number | null
  >
  const allValid = TERMS_FIELDS.every((f) => parsed[f] !== null)
  const dirty = TERMS_FIELDS.some(
    (f) => values[f].trim() === '' || Number(values[f]) !== Number(base[f]),
  )
  const nextValues = allValid ? (parsed as TermsValues) : null

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
            <Badge variant={enabled ? 'success' : 'neutral'}>
              {enabled ? t.status.on : t.status.off}
            </Badge>
          </div>
          <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t.versionLine(current.version, fmtDate(current.created_at), current.created_by_email)}
          </p>
        </div>
        <Button variant={enabled ? 'outline' : 'primary'} size="sm" onClick={() => setSwitching(true)}>
          {enabled ? t.switch.turnOff : t.switch.turnOn}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {TERMS_GROUPS.map((group) => (
          <fieldset
            key={group.id}
            className="rounded-[var(--radius-panel)] border border-[var(--color-border)] p-3"
          >
            <legend className="px-1 text-sm font-medium text-[var(--color-text)]">
              {t.groups[group.id]}
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {group.fields.map((field) => (
                <Field
                  key={field}
                  id={`rewards-${field}`}
                  label={`${t.fields[field]} (${t.units[UNIT[field]]})`}
                  error={parsed[field] === null ? t.invalid : undefined}
                >
                  <Input
                    type="number"
                    inputMode={FIELD_KIND[field] === 'count' ? 'numeric' : 'decimal'}
                    min={0}
                    step={FIELD_KIND[field] === 'count' ? 1 : 0.01}
                    value={values[field]}
                    onChange={(e) => {
                      setSavedVersion(null)
                      setDraft({ ...values, [field]: e.target.value })
                    }}
                    className="tabular-nums"
                  />
                </Field>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          disabled={!dirty || !allValid}
          onClick={() => setSaving(true)}
        >
          {t.save}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(null)}>
            {t.reset}
          </Button>
        )}
        {savedVersion !== null && (
          <span role="status" className="text-sm text-[var(--color-success)]">
            {t.saved(savedVersion)}
          </span>
        )}
      </div>

      <TrialSetting days={default_trial_days} t={t} />
      <History versions={versions} t={t} />

      {switching && <SwitchDialog enabled={enabled} onClose={() => setSwitching(false)} t={t} />}
      {saving && nextValues && (
        <SaveDialog
          current={current}
          values={nextValues}
          onClose={() => setSaving(false)}
          onSaved={async (row) => {
            setSaving(false)
            setDraft(null)
            setSavedVersion(row.version)
            await queryClient.invalidateQueries({ queryKey: termsKey })
          }}
          t={t}
        />
      )}
    </Card>
  )
}
